import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/AuthContext";
import { billingApi, formatError } from "../lib/api";

/**
 * BillingSuccess — landing page after a successful (or cancelled) Stripe Checkout.
 *
 * Stripe redirects here with `?session_id=cs_test_...`. We poll the backend
 * `/billing/status/:sessionId` endpoint up to 8 times (every 2 sec) which:
 *   1. Asks Stripe for the latest payment status
 *   2. On the first `paid` hit, extends the user's `premium_until` by 30 days
 *      and flips `is_premium=true`
 *   3. Returns idempotently on subsequent polls (already_processed=true)
 *
 * Once we see a paid status we refresh the auth context so the rest of the
 * app immediately sees the user as Premium without a full reload.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 8;

const StatusCard = ({ icon: Icon, iconBg, iconFg, title, body, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className="tup-card p-7 text-center"
    data-testid="billing-status-card"
  >
    <div className={`w-16 h-16 ${iconBg} ${iconFg} rounded-2xl mx-auto flex items-center justify-center`}>
      <Icon className="w-8 h-8" />
    </div>
    <h1 className="font-heading text-2xl font-bold text-[#2D3142] mt-5">{title}</h1>
    <p className="text-[#4F5D75] mt-2 leading-relaxed">{body}</p>
    {children}
  </motion.div>
);

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [phase, setPhase] = useState("checking"); // checking | paid | failed | timeout
  const [errorMsg, setErrorMsg] = useState("");
  const pollsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      setErrorMsg("Missing payment session — please return to your profile and try again.");
      return;
    }

    let timer = null;
    const poll = async () => {
      if (stoppedRef.current) return;
      try {
        const res = await billingApi.status(sessionId);
        if (res.payment_status === "paid") {
          stoppedRef.current = true;
          await refresh(); // pull updated is_premium / premium_until into AuthContext
          setPhase("paid");
          return;
        }
        if (res.status === "expired") {
          stoppedRef.current = true;
          setPhase("failed");
          setErrorMsg("Your Stripe session expired before payment was completed.");
          return;
        }
        // Still pending — keep polling
        pollsRef.current += 1;
        if (pollsRef.current >= MAX_POLLS) {
          stoppedRef.current = true;
          setPhase("timeout");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        stoppedRef.current = true;
        setPhase("failed");
        setErrorMsg(formatError(err));
      }
    };
    poll();

    return () => {
      stoppedRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, refresh]);

  return (
    <div className="px-5 pt-10 pb-8" data-testid="billing-success-page">
      {phase === "checking" && (
        <StatusCard
          icon={Loader2}
          iconBg="bg-[#DCEDF0]"
          iconFg="text-[#1F5566] animate-spin"
          title="Confirming your payment…"
          body="Stripe is letting us know it went through. This usually takes a few seconds."
        />
      )}

      {phase === "paid" && (
        <StatusCard
          icon={CheckCircle2}
          iconBg="brand-gradient-bg"
          iconFg="text-white"
          title="You're Premium 🎉"
          body="Thanks for supporting QFresh. Your premium access is active for the next 30 days — renew any time from your Profile."
        >
          <div className="mt-6 flex flex-col gap-2.5">
            <Button
              onClick={() => navigate("/")}
              data-testid="billing-go-home-btn"
              className="w-full brand-gradient-bg text-white h-12"
            >
              Open QFresh
            </Button>
            <Link
              to="/profile"
              data-testid="billing-go-profile-link"
              className="text-sm text-[#8A93A6] hover:text-[#277A82] py-2"
            >
              Manage your plan
            </Link>
          </div>
        </StatusCard>
      )}

      {phase === "timeout" && (
        <StatusCard
          icon={Loader2}
          iconBg="bg-[#F5F3ED]"
          iconFg="text-[#8A93A6]"
          title="Still processing"
          body="Stripe is taking a little longer than usual. Your payment will be reflected automatically — you can safely close this page."
        >
          <Button
            onClick={() => navigate("/profile")}
            data-testid="billing-back-to-profile-btn"
            className="w-full mt-6 bg-[#1F5566] text-white h-12"
          >
            Back to Profile
          </Button>
        </StatusCard>
      )}

      {phase === "failed" && (
        <StatusCard
          icon={AlertCircle}
          iconBg="bg-[#FCE7DD]"
          iconFg="text-[#9C3E1D]"
          title="Payment didn't go through"
          body={errorMsg || "We couldn't confirm your payment. No charge has been made — you can try again any time."}
        >
          <Button
            onClick={() => navigate("/profile")}
            data-testid="billing-retry-btn"
            className="w-full mt-6 bg-[#1F5566] text-white h-12"
          >
            Back to Profile
          </Button>
        </StatusCard>
      )}
    </div>
  );
};

export default BillingSuccess;
